import {
  AdminGrid,
  AdminPageHeader,
  Button,
  Card,
  CardHeader,
  InlineAlert,
  PostureBar,
  PostureCell,
  PostureHead,
  ProgressBar,
  SectionHeader,
  SkeletonRows,
} from "@constructorfleet/ui/design-system";
import { useToast } from "@constructorfleet/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import type {
  BhgCallSettings,
  BhgPhraseTrigger,
  BhgResultTrigger,
  ProductDbClient,
} from "@constructorfleet/bhg-product-core";
import {
  loadBhgCallAdminSettings,
  normalizeDialString,
  normalizeStringList,
  saveBhgPhraseRules,
  saveBhgResultRules,
} from "@constructorfleet/bhg-product-core";
import { ActiveCallsPanel } from "./admin-settings/active-calls-panel";
import { CallHistoryPanel } from "./admin-settings/call-history-panel";
import { NextCallCard } from "./admin-settings/next-call-card";
import { PhraseTriggerEditor } from "./admin-settings/phrase-trigger-editor";
import { ResultTriggerEditor } from "./admin-settings/result-trigger-editor";
import { RuntimeSettingsForm } from "./admin-settings/runtime-settings-form";
import { SlidePanel } from "./admin-settings/slide-panel";
import { TriggerCallCard } from "./admin-settings/trigger-call-card";

type ServiceApplicationRecord = {
  id: string;
  userId: number;
  user: {
    id: number;
    username: string;
    email: string | null;
  };
  productId: string;
  requestedType: "discount" | "comp" | (string & {});
  requestedDiscountPercent: number | null;
  reason: string;
  status: "pending" | "approved" | "rejected" | (string & {});
  approvedDiscountPercent: number | null;
  reviewedByUserId: number | null;
  reviewedAt: string | null;
  decisionNotes: string;
  createdAt: string;
  updatedAt: string;
  supportingDocumentS3Key: string | null;
};

type ClinicSyncStatus = {
  state: "idle" | "running" | "succeeded" | "failed";
  startedAt: string | null;
  finishedAt: string | null;
  result: { total?: number; upserted?: number; errors?: number } | null;
  error: string | null;
};

/* ── helpers ──────────────────────────────────────────────────── */

function serialize(value: unknown) {
  return JSON.stringify(value);
}

function nextRuleId(existingIds: string[], prefix: string) {
  let index = existingIds.length + 1;
  while (existingIds.includes(`${prefix}_${index}`)) {
    index += 1;
  }
  return `${prefix}_${index}`;
}

function formatProviderLabel(provider: BhgCallSettings["voiceProvider"]) {
  return provider === "telnyx" ? "Telnyx" : "Twilio";
}

function getRuntimeDraftLabel(isRuntimeDirty: boolean) {
  return isRuntimeDirty ? "Draft needs review" : "Ready";
}

function getAutomationDraftLabel(isDirty: boolean) {
  return isDirty ? "Unsaved changes" : "Saved";
}

export function resolveManualHangupCallSid(input: {
  selectedCallSid: string;
  manualCallSid: string;
}) {
  const manualCallSid = input.manualCallSid.trim();
  if (manualCallSid) {
    return manualCallSid;
  }
  return input.selectedCallSid.trim();
}

export { formatManualHangupMessage } from "./admin-settings/active-calls-panel";

/* ── main page ────────────────────────────────────────────────── */

function usePostureClock() {
  const [now, setNow] = useState(() => new Date());
  const ref = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    ref.current = setInterval(() => setNow(new Date()), 1000);
    return () => {
      if (ref.current) clearInterval(ref.current);
    };
  }, []);
  const utc = now.toISOString().slice(11, 19) + " UTC";
  let tz: string;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    tz = "Local";
  }
  const local =
    now.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }) +
    " " +
    tz;
  return `${utc} · ${local}`;
}

export function BhgCallAdminSettingsPage({ dbClient }: { dbClient: ProductDbClient }) {
  const { pushToast } = useToast();
  const clockSub = usePostureClock();
  const [settings, setSettings] = useState<BhgCallSettings | null>(null);
  const [phraseRules, setPhraseRules] = useState<BhgPhraseTrigger[]>([]);
  const [resultRules, setResultRules] = useState<BhgResultTrigger[]>([]);

  const [runtimeInitial, setRuntimeInitial] = useState<string | null>(null);
  const [phraseInitial, setPhraseInitial] = useState<string | null>(null);
  const [resultInitial, setResultInitial] = useState<string | null>(null);

  const [phrasePanel, setPhrasePanel] = useState(false);
  const [resultPanel, setResultPanel] = useState(false);
  const [openPhraseIndex, setOpenPhraseIndex] = useState<number | null>(null);
  const [openResultIndex, setOpenResultIndex] = useState<number | null>(null);

  const [clinicSyncStatus, setClinicSyncStatus] = useState<ClinicSyncStatus | null>(null);
  const [serviceApplications, setServiceApplications] = useState<ServiceApplicationRecord[]>([]);
  const [loadingServiceApplications, setLoadingServiceApplications] = useState(false);
  const [serviceApplicationsActionId, setServiceApplicationsActionId] = useState<string | null>(
    null
  );
  const [serviceApplicationsMessage, setServiceApplicationsMessage] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      const loaded = await loadBhgCallAdminSettings(dbClient);
      if (!mounted) return;
      setSettings(loaded.runtimeSettings);
      setPhraseRules(loaded.phraseRules);
      setResultRules(loaded.resultRules);
      setRuntimeInitial(serialize(loaded.runtimeSettings));
      setPhraseInitial(serialize(loaded.phraseRules));
      setResultInitial(serialize(loaded.resultRules));
    })();
    return () => {
      mounted = false;
    };
  }, [dbClient]);

  useEffect(() => {
    let mounted = true;
    const requestAdmin = dbClient.requestAdmin;
    if (!requestAdmin) {
      return;
    }

    const loadClinicSyncStatus = async () => {
      const response = await requestAdmin<{ status?: ClinicSyncStatus }>({
        method: "GET",
        path: "/api/admin/bhg-call/sync-clinics/status",
      });
      if (mounted && response.ok) {
        setClinicSyncStatus(response.data.status ?? null);
      }
    };

    void loadClinicSyncStatus();
    return () => {
      mounted = false;
    };
  }, [dbClient]);

  useEffect(() => {
    const requestAdmin = dbClient.requestAdmin;
    if (!requestAdmin || clinicSyncStatus?.state !== "running") {
      return;
    }

    let cancelled = false;
    const poll = async () => {
      const response = await requestAdmin<{ status?: ClinicSyncStatus }>({
        method: "GET",
        path: "/api/admin/bhg-call/sync-clinics/status",
      });
      if (!cancelled && response.ok) {
        setClinicSyncStatus(response.data.status ?? null);
      }
    };

    const interval = window.setInterval(() => {
      void poll();
    }, 2_000);

    void poll();
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [clinicSyncStatus?.state, dbClient]);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      if (!dbClient.requestAdmin) {
        return;
      }
      setLoadingServiceApplications(true);
      try {
        const response = await dbClient.requestAdmin<{ applications?: ServiceApplicationRecord[] }>(
          {
            method: "GET",
            path: "/api/admin/bhg-call/assistance-applications",
          }
        );
        if (mounted && response.ok) {
          setServiceApplications(response.data.applications ?? []);
        }
      } finally {
        if (mounted) {
          setLoadingServiceApplications(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [dbClient]);

  const isRuntimeDirty = useMemo(() => {
    if (!settings || !runtimeInitial) return false;
    return serialize(settings) !== runtimeInitial;
  }, [runtimeInitial, settings]);

  const isPhraseDirty = useMemo(() => {
    if (!phraseInitial) return false;
    return serialize(phraseRules) !== phraseInitial;
  }, [phraseInitial, phraseRules]);

  const isResultDirty = useMemo(() => {
    if (!resultInitial) return false;
    return serialize(resultRules) !== resultInitial;
  }, [resultInitial, resultRules]);
  const isClinicSyncRunning = clinicSyncStatus?.state === "running";

  const refreshServiceApplications = async () => {
    if (!dbClient.requestAdmin) return;
    const response = await dbClient.requestAdmin<{ applications?: ServiceApplicationRecord[] }>({
      method: "GET",
      path: "/api/admin/bhg-call/assistance-applications",
    });
    if (response.ok) {
      setServiceApplications(response.data.applications ?? []);
    }
  };

  const handleApplicationDecision = async (
    application: ServiceApplicationRecord,
    status: "approved" | "rejected"
  ) => {
    if (!dbClient.requestAdmin) {
      return;
    }
    setServiceApplicationsActionId(application.id);
    setServiceApplicationsMessage(null);
    try {
      const approvedDiscountPercent =
        status === "approved"
          ? application.requestedType === "comp"
            ? 100
            : (application.requestedDiscountPercent ?? 25)
          : null;
      const response = await dbClient.requestAdmin<{ application?: ServiceApplicationRecord }>({
        method: "POST",
        path: "/api/admin/bhg-call/assistance-applications",
        body: {
          id: application.id,
          status,
          approvedDiscountPercent,
          decisionNotes:
            status === "approved"
              ? `Approved at ${approvedDiscountPercent}%`
              : "Rejected by admin.",
          userId: application.userId,
        },
      });
      if (!response.ok) {
        setServiceApplicationsMessage(response.error);
        return;
      }
      await refreshServiceApplications();
      setServiceApplicationsMessage(
        status === "approved" ? "Application approved." : "Application rejected."
      );
    } catch {
      setServiceApplicationsMessage("Unable to update application.");
    } finally {
      setServiceApplicationsActionId(null);
    }
  };

  if (!settings) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <ProgressBar label="Loading settings" />
          <p style={{ marginTop: "var(--cf-space-sm)" }}>Loading BHG app settings...</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="page">
      <AdminPageHeader
        title="BHG Check-in Assist Admin"
        description="Run live call operations, intervene quickly, and manage the automation baseline."
      />

      <PostureBar>
        <PostureHead
          label="Runtime posture"
          title="BHG Call Admin"
          sub={
            <>
              <span>{getRuntimeDraftLabel(isRuntimeDirty)}</span>
              <br />
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 10,
                  letterSpacing: "0.02em",
                }}
              >
                {clockSub}
              </span>
            </>
          }
        />
        <PostureCell
          label="Voice provider"
          value={formatProviderLabel(settings.voiceProvider)}
          dot="ok"
        />
        <PostureCell
          label="Phrase rules"
          value={phraseRules.length}
          dot={isPhraseDirty ? "warn" : "ok"}
          sub={getAutomationDraftLabel(isPhraseDirty)}
        />
        <PostureCell
          label="Result rules"
          value={resultRules.length}
          dot={isResultDirty ? "warn" : "ok"}
          sub={getAutomationDraftLabel(isResultDirty)}
        />
        <NextCallCard dbClient={dbClient} />
      </PostureBar>

      <SectionHeader
        title="Live Operations"
        description="Watch active traffic, reconcile stale calls, and audit recent outcomes without leaving the page."
      />
      <AdminGrid cols={2}>
        <ActiveCallsPanel dbClient={dbClient} />
        <TriggerCallCard dbClient={dbClient} />
      </AdminGrid>
      <CallHistoryPanel dbClient={dbClient} />

      <SectionHeader
        title="Assistance Applications"
        description="Review discount and comp requests before you adjust access or billing."
      />
      <Card wide>
        <CardHeader title="Applications" meta={`${serviceApplications.length} total`} />
        <div className="card-body ds-stack">
          {serviceApplicationsMessage && (
            <InlineAlert tone="info">{serviceApplicationsMessage}</InlineAlert>
          )}
          {loadingServiceApplications ? (
            <div>
              <SkeletonRows count={2} />
            </div>
          ) : serviceApplications.length === 0 ? (
            <p className="hc-desc">No assistance applications yet.</p>
          ) : (
            <div className="ds-stack">
              {serviceApplications.map((application) => (
                <Card key={application.id}>
                  <div className="card-body ds-stack">
                    <div className="ds-between">
                      <strong>{application.user.username}</strong>
                      <span>{application.status}</span>
                    </div>
                    <p className="hc-desc">
                      {application.user.email ?? `User ${application.userId}`}
                    </p>
                    <p>{application.reason}</p>
                    <p className="hc-desc">
                      Requested {application.requestedType}
                      {application.requestedDiscountPercent
                        ? ` (${application.requestedDiscountPercent}%)`
                        : ""}
                    </p>
                    {application.approvedDiscountPercent ? (
                      <p className="hc-desc">Approved at {application.approvedDiscountPercent}%</p>
                    ) : null}
                    {application.decisionNotes ? (
                      <p className="hc-desc">{application.decisionNotes}</p>
                    ) : null}
                    {application.supportingDocumentS3Key && (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={async () => {
                          if (!dbClient.requestAdmin) return;
                          const response = await dbClient.requestAdmin<{ url?: string }>({
                            method: "GET",
                            path: `/api/admin/bhg-call/assistance-applications/document?s3Key=${encodeURIComponent(application.supportingDocumentS3Key!)}`,
                          });
                          if (response.ok && response.data.url) {
                            window.open(response.data.url, "_blank");
                          }
                        }}
                      >
                        View document
                      </Button>
                    )}
                    {application.status === "pending" && (
                      <div className="shell-actions">
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          disabled={serviceApplicationsActionId === application.id}
                          onClick={() => void handleApplicationDecision(application, "approved")}
                        >
                          {serviceApplicationsActionId === application.id ? "..." : "Approve"}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          className="btn-danger"
                          disabled={serviceApplicationsActionId === application.id}
                          onClick={() => void handleApplicationDecision(application, "rejected")}
                        >
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Card>

      <SectionHeader
        title="Automation Controls"
        description="Handle one-off interventions and keep the trigger logic that drives automated outreach in sync."
      />
      <AdminGrid cols={3}>
        <Card>
          <CardHeader title="Clinic Directory" />
          <div className="card-body ds-stack">
            <p className="hc-desc">
              Pull the latest clinic locations from BHG Recovery and upsert them into the local
              database.
            </p>
            {clinicSyncStatus?.state === "running" ? (
              <InlineAlert tone="info">Clinic sync is running in the background.</InlineAlert>
            ) : null}
            {clinicSyncStatus?.state === "succeeded" && clinicSyncStatus.result ? (
              <InlineAlert tone={clinicSyncStatus.result.errors ? "info" : "success"}>
                {`Clinic directory updated: ${clinicSyncStatus.result.upserted ?? 0} upserted of ${clinicSyncStatus.result.total ?? 0} (${clinicSyncStatus.result.errors ?? 0} error${(clinicSyncStatus.result.errors ?? 0) === 1 ? "" : "s"}).`}
              </InlineAlert>
            ) : null}
            {clinicSyncStatus?.state === "failed" && clinicSyncStatus.error ? (
              <InlineAlert tone="danger">{`Clinic sync failed: ${clinicSyncStatus.error}`}</InlineAlert>
            ) : null}
            <Button
              type="button"
              disabled={isClinicSyncRunning || !dbClient.requestAdmin}
              onClick={() => {
                void (async () => {
                  const response = await dbClient.requestAdmin!<{
                    started?: boolean;
                    status?: ClinicSyncStatus;
                  }>({
                    method: "POST",
                    path: "/api/admin/bhg-call/sync-clinics",
                  });
                  if (!response.ok) {
                    pushToast(response.error, "danger");
                    return;
                  }
                  setClinicSyncStatus(response.data.status ?? null);
                  pushToast(
                    response.data.started === false
                      ? "Clinic sync is already running."
                      : "Clinic sync started in the background.",
                    "info"
                  );
                })();
              }}
            >
              {isClinicSyncRunning ? "Sync Running…" : "Sync Clinics"}
            </Button>
          </div>
        </Card>
        <Card>
          <CardHeader title="Phrase Triggers" />
          <div className="card-body ds-stack">
            <p className="hc-desc">
              {phraseRules.length} trigger{phraseRules.length !== 1 ? "s" : ""} configured.{" "}
              {isPhraseDirty ? "Unsaved edits are open." : "Everything here is saved."}
            </p>
            <Button type="button" onClick={() => setPhrasePanel(true)}>
              Review phrase triggers
            </Button>
            <div className="sec-hdr">
              <h2>Result Rules</h2>
            </div>
            <p className="hc-desc">
              {resultRules.length} rule{resultRules.length !== 1 ? "s" : ""} configured.{" "}
              {isResultDirty ? "Unsaved edits are open." : "Everything here is saved."}
            </p>
            <Button type="button" onClick={() => setResultPanel(true)}>
              Review result rules
            </Button>
          </div>
        </Card>
      </AdminGrid>

      <SectionHeader
        title="Configuration Baseline"
        description="Adjust routing, call safety limits, and provider credentials that define the default runtime posture."
      />
      <RuntimeSettingsForm
        settings={settings}
        setSettings={setSettings}
        isRuntimeDirty={isRuntimeDirty}
        onSaved={() => setRuntimeInitial(serialize(settings))}
        dbClient={dbClient}
      />

      {/* ── phrase triggers panel ───────────────────────────────── */}
      <SlidePanel
        open={phrasePanel}
        title={`Phrase Triggers (${phraseRules.length})`}
        onClose={() => setPhrasePanel(false)}
        actions={
          <>
            <Button
              type="button"
              size="sm"
              variant="brand"
              onClick={() => {
                const nextIndex = phraseRules.length;
                const nextId = nextRuleId(
                  phraseRules.map((rule) => rule.id),
                  "phrase_trigger"
                );
                setPhraseRules((current) => [
                  ...current,
                  {
                    id: nextId,
                    phrasePatterns: [""],
                    verificationPhrases: [],
                    requiredIds: [],
                    resetIdsOnInvalid: [],
                    sendDigits: null,
                    sendDigitsWhenInvalid: null,
                    pauseSeconds: null,
                    recordSeconds: null,
                    oneshot: false,
                    hangup: false,
                    priority: 0,
                  },
                ]);
                setOpenPhraseIndex(nextIndex);
              }}
            >
              Add trigger
            </Button>
            <Button
              type="button"
              disabled={!isPhraseDirty}
              onClick={() => {
                const nextRules = phraseRules.map((rule) => ({
                  ...rule,
                  id: rule.id.trim(),
                  phrasePatterns: normalizeStringList(rule.phrasePatterns),
                  verificationPhrases: normalizeStringList(rule.verificationPhrases),
                  requiredIds: normalizeStringList(rule.requiredIds),
                  resetIdsOnInvalid: normalizeStringList(rule.resetIdsOnInvalid),
                  sendDigits: normalizeDialString(rule.sendDigits),
                  sendDigitsWhenInvalid: normalizeDialString(rule.sendDigitsWhenInvalid),
                }));
                void (async () => {
                  const result = await saveBhgPhraseRules(nextRules, dbClient);
                  if (result.ok) {
                    setPhraseRules(nextRules);
                    setPhraseInitial(serialize(nextRules));
                    pushToast("Phrase triggers saved.", "success");
                  } else {
                    pushToast(result.error, "danger");
                  }
                })();
              }}
            >
              Save phrase triggers
            </Button>
          </>
        }
      >
        {isPhraseDirty ? <InlineAlert tone="info">You have unsaved changes.</InlineAlert> : null}
        <PhraseTriggerEditor
          phraseRules={phraseRules}
          setPhraseRules={setPhraseRules}
          openIndex={openPhraseIndex}
          setOpenIndex={setOpenPhraseIndex}
        />
      </SlidePanel>
      {/* ── result triggers panel ───────────────────────────────── */}
      <SlidePanel
        open={resultPanel}
        title={`Result Rules (${resultRules.length})`}
        onClose={() => setResultPanel(false)}
        actions={
          <>
            <Button
              type="button"
              size="sm"
              variant="brand"
              onClick={() => {
                const nextIndex = resultRules.length;
                const nextId = nextRuleId(
                  resultRules.map((rule) => rule.id),
                  "result_trigger"
                );
                setResultRules((current) => [
                  ...current,
                  {
                    id: nextId,
                    label: "New result trigger",
                    phrasePatterns: [""],
                    isScheduledTrigger: false,
                    priority: 0,
                  },
                ]);
                setOpenResultIndex(nextIndex);
              }}
            >
              Add rule
            </Button>
            <Button
              type="button"
              disabled={!isResultDirty}
              onClick={() => {
                const nextRules = resultRules.map((rule) => ({
                  ...rule,
                  id: rule.id.trim(),
                  label: rule.label.trim(),
                  phrasePatterns: normalizeStringList(rule.phrasePatterns),
                }));
                void (async () => {
                  const result = await saveBhgResultRules(nextRules, dbClient);
                  if (result.ok) {
                    setResultRules(nextRules);
                    setResultInitial(serialize(nextRules));
                    pushToast("Result rules saved.", "success");
                  } else {
                    pushToast(result.error, "danger");
                  }
                })();
              }}
            >
              Save result rules
            </Button>
          </>
        }
      >
        {isResultDirty ? <InlineAlert tone="info">You have unsaved changes.</InlineAlert> : null}
        <ResultTriggerEditor
          resultRules={resultRules}
          setResultRules={setResultRules}
          openIndex={openResultIndex}
          setOpenIndex={setOpenResultIndex}
        />
      </SlidePanel>
    </div>
  );
}
